import { action, makeObservable, observable } from 'mobx';
import { Filter, FilterMeta } from './Search';
import { SearchService, SEARCH_SERVICE } from './service';
import { StorageInteractor, STORAGE_INTERACTOR_KEY } from '../utils/storageInteractor';

import { injector } from 'Injector';
export class SearchStore {
  private searchService: SearchService = injector.get<SearchService>(SEARCH_SERVICE);
  private storageInteractor = injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY);
  @observable public paginationTotalPages: number = 1;
  @observable public useFilters: boolean = false;
  @observable public isFilters: boolean = false;
  @observable public myfilterLang: string | null = null;
  @observable public myfilter: Filter = {
    page: 1,
    per_page: 24,
    localeId: null,
    type: 1,
    grades: [],
    subjects: [],
    coreElements: [],
    topics: [],
    goals: [],
    sources: [],
    readingInSubjects: [],
    searchQuery: ''
  };
  @observable public getFilters?: FilterMeta;

  constructor() {
    makeObservable(this);
  }

  /**
   * The two fields every search writes when its response arrives.
   *
   * They were assigned directly from the components, after an `await` — so outside any
   * action, twice per search, in ten places. An action is the better home than a
   * `runInAction` at each call site: it batches the pair, and there is now one place that
   * knows a search result updates both. Class D of docs/mobx-6-class-d.md.
   */
  @action
  public setSearchResults(totalPages: number, filters: FilterMeta) {
    this.paginationTotalPages = totalPages;
    this.getFilters = filters;
  }

  @action
  public setLanguageFilter(value: string) {
    this.myfilterLang = value;
  }

  /**
   * One locale chosen in the filter panel — which is two fields: the filter the panel shows
   * as selected, and the `localeId` the next request is sent with.
   *
   * Separate from `setLanguageFilter` above on purpose: that one takes a comma-joined *list*
   * of locales from the language buttons, and does not touch the query. These were assigned
   * on consecutive lines from the panel, outside any action, and both are observed.
   */
  @action
  public setSearchLocale(value: string) {
    this.myfilterLang = value;
    this.myfilter.localeId = value;
  }

  @action
  public async getDataSearch() {
    this.validIfUsed();
    const data = this.searchService.getDataSearch(this.myfilter);
    this.isFilters = false;
    return data;
  }
  @action
  public resetFilters() {
    this.myfilter = {
      page: 1,
      per_page: 24,
      localeId: this.myfilterLang,
      type: 1,
      grades: [],
      subjects: [],
      coreElements: [],
      topics: [],
      goals: [],
      sources: [],
      readingInSubjects: [],
      searchQuery: ''
    };
  }
  // `@action` like `resetFilters` above, which it is a near-copy of. It was missing one.
  @action
  public resetLangFilters() {
    this.myfilter = {
      page: 1,
      per_page: 24,
      localeId: '',
      type: 1,
      grades: [],
      subjects: [],
      coreElements: [],
      topics: [],
      goals: [],
      sources: [],
      readingInSubjects: [],
    };
  }
  @action
  public validIfUsed() {
    const filter = this.myfilter;
    const anySelected = filter && [
      filter.grades, filter.subjects, filter.coreElements, filter.topics, filter.goals, filter.sources, filter.readingInSubjects,
    ].some(ids => ids!.length > 0);
    if (anySelected) {
      this.useFilters = true;
    }
  }
}
